import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLine } from "./Reveal";
import { RichText, LexLinkScope } from "./RichText";

export const H2 = ({ children, id }) => (
  <h2
    id={id}
    className="mt-16 mb-8 font-serif text-3xl font-normal leading-snug tracking-tight text-ink first:mt-0 sm:text-4xl"
  >
    {children}
  </h2>
);

export const H3 = ({ children }) => (
  <h3 className="mt-10 font-serif text-2xl font-medium leading-snug text-ink">{children}</h3>
);

export const P = ({ children }) => (
  <p className="mt-5 text-base font-light leading-relaxed text-body sm:text-lg">
    {typeof children === "string" ? <RichText text={children} /> : children}
  </p>
);

export const SeeAlso = ({ links }) => (
  <aside className="mt-16 border border-ink/15 bg-cream p-8">
    <p className="eyebrow">Pour aller plus loin</p>
    <ul className="mt-5 space-y-3">
      {links.map((l) => (
        <li key={l.to}>
          <Link
            to={l.to}
            data-testid={`seealso-${l.to.replace(/\//g, "")}`}
            className="group inline-flex items-center gap-3 font-serif text-xl text-ink transition-colors hover:text-bronze"
          >
            <ArrowRight className="h-4 w-4 text-bronze transition-transform duration-300 group-hover:translate-x-1" />
            <span className="link-underline">{l.label}</span>
          </Link>
        </li>
      ))}
    </ul>
  </aside>
);

export default function ArticleShell({ eyebrow, titleLines, lead, image, testId, children }) {
  return (
    <article data-testid={testId} className="bg-paper pb-24 pt-28 sm:pt-36">
      <header className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal y={16}>
          <p className="eyebrow">{eyebrow}</p>
        </Reveal>
        <h1 className="mt-6 max-w-4xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
          {titleLines.map((line, i) => (
            <MaskedLine key={i} delay={0.1 + i * 0.12}>
              {line}
              {i < titleLines.length - 1 ? " " : ""}
            </MaskedLine>
          ))}
        </h1>
        <Reveal delay={0.35}>
          <p className="mt-8 max-w-2xl text-lg font-light leading-relaxed text-smoke sm:text-xl">
            {lead}
          </p>
        </Reveal>
      </header>

      {image && (
        <Reveal delay={0.15} className="mx-auto mt-14 max-w-7xl px-6 lg:px-10">
          <figure className="img-content-figure">
            {image.to ? (
              <Link
                to={image.to}
                data-testid="article-image-link"
                className="group block"
              >
                <div className="img-content-wrap">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className={`img-content border border-ink/15${image.className ? ` ${image.className}` : ""}`}
                    loading="eager"
                  />
                </div>
              </Link>
            ) : (
              <div className="img-content-wrap">
                <img
                  src={image.src}
                  alt={image.alt}
                  className={`img-content border border-ink/15${image.className ? ` ${image.className}` : ""}`}
                  loading="eager"
                />
              </div>
            )}
            <figcaption
            className={`mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke${
              image.className && image.className.includes("mx-auto") ? " text-center" : ""
            }`}
          >
              {image.caption}
              {image.credit && (
                <>
                  {" "}
                  <a
                    href={image.creditUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="article-image-credit"
                    className="normal-case text-bronze underline-offset-4 transition-colors hover:underline"
                  >
                    {image.credit}
                  </a>
                </>
              )}
            </figcaption>
          </figure>
        </Reveal>
      )}

      <Reveal className="mx-auto mt-16 max-w-3xl px-6 lg:px-10"><LexLinkScope>{children}</LexLinkScope></Reveal>
    </article>
  );
}
